import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Visibility, Search } from '@mui/icons-material';
import { ProgramSubscription } from '../../services/pipelineService';
import { fetchSubscriptions } from '../../services/pipelineService';
import SubscriptionDialog from './SubscriptionDialog';
import SubscriptionDetailDialog from './SubscriptionDetailDialog';

const SubscriptionsTable: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<ProgramSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<ProgramSubscription | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadSubscriptions();
  }, [statusFilter]);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = statusFilter !== 'all' ? { status: statusFilter } : undefined;
      const data = await fetchSubscriptions(filters);
      setSubscriptions(data);
    } catch (err) {
      console.error('Error loading subscriptions:', err);
      setError('Failed to load subscriptions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubscription = () => {
    setCreateDialogOpen(true);
  };

  const handleViewSubscription = (subscription: ProgramSubscription) => {
    setSelectedSubscription(subscription);
    setDetailDialogOpen(true);
  };

  const handleCreateDialogClose = async (success: boolean) => {
    setCreateDialogOpen(false);
    if (success) {
      await loadSubscriptions();
    }
  };

  const handleDetailDialogClose = async (refresh: boolean) => {
    setDetailDialogOpen(false);
    setSelectedSubscription(null);
    if (refresh) {
      await loadSubscriptions();
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'default';
      default:
        return 'default';
    }
  };

  const filteredSubscriptions = subscriptions.filter((sub) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      sub.client_name?.toLowerCase().includes(query) ||
      sub.client_email?.toLowerCase().includes(query) ||
      sub.program_name?.toLowerCase().includes(query) ||
      sub.program_display_name?.toLowerCase().includes(query)
    );
  });

  const columns: GridColDef[] = [
    {
      field: 'client_name',
      headerName: 'Client',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.client_name || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.client_email || ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'program_display_name',
      headerName: 'Program',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'subscription_status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value?.charAt(0).toUpperCase() + params.value?.slice(1) || 'Unknown'}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'start_date',
      headerName: 'Start Date',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'next_billing_date',
      headerName: 'Next Billing',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'custom_monthly_fee',
      headerName: 'Monthly Fee',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          ${parseFloat(params.value || '0').toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => handleViewSubscription(params.row)}
            title="View Details"
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', height: 600 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">Client Subscriptions</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddSubscription}
        >
          Add Subscription
        </Button>
      </Box>

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          placeholder="Search by client or program..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1}>
          <Chip
            label="All"
            onClick={() => setStatusFilter('all')}
            color={statusFilter === 'all' ? 'primary' : 'default'}
            clickable
          />
          <Chip
            label="Active"
            onClick={() => setStatusFilter('active')}
            color={statusFilter === 'active' ? 'success' : 'default'}
            clickable
          />
          <Chip
            label="Paused"
            onClick={() => setStatusFilter('paused')}
            color={statusFilter === 'paused' ? 'warning' : 'default'}
            clickable
          />
          <Chip
            label="Cancelled"
            onClick={() => setStatusFilter('cancelled')}
            color={statusFilter === 'cancelled' ? 'error' : 'default'}
            clickable
          />
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 400,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={filteredSubscriptions}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-cell': {
              py: 1,
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        />
      )}

      <SubscriptionDialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
      />

      {selectedSubscription && (
        <SubscriptionDetailDialog
          open={detailDialogOpen}
          subscription={selectedSubscription}
          onClose={handleDetailDialogClose}
        />
      )}
    </Box>
  );
};

export default SubscriptionsTable;
